import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { signInWithGoogle, signOut } from '../services/auth';
import { getUser, getPublicUser } from '../services/DataStore';
import { User } from '../models/User';
import { PublicUser } from '../models/PublicUser';
import { debug } from '../utils/debug';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const fetchUserDataWithRetry = async (userId, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      debug.log(`Attempt ${i + 1} to fetch user data...`);
      const userData = await getUser(userId);
      const publicUserData = await getPublicUser(userId);
      
      if (userData && publicUserData) {
        debug.log('Successfully fetched user data');
        return { userData, publicUserData };
      }
      
      // Wait before next retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      debug.error(`Attempt ${i + 1} failed:`, error);
      if (i === maxRetries - 1) throw error;
      // Wait before next retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }
  throw new Error('Failed to fetch user data after all retries');
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [publicUserData, setPublicUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleSignIn = async () => {
    try {
      const { user, userData: newUserData, publicUserData: newPublicUserData } = await signInWithGoogle();
      setCurrentUser(user);
      
      // If we have new user data from the cloud function, use it
      if (newUserData && newPublicUserData) {
        debug.log('Using new user data from cloud function');
        setUserData(new User(newUserData));
        setPublicUserData(new PublicUser(newPublicUserData));
      } else {
        // If no new data, try to fetch with retries
        debug.log('No new user data, attempting to fetch with retries...');
        const { userData: fetchedUserData, publicUserData: fetchedPublicUserData } = 
          await fetchUserDataWithRetry(user.uid);
        
        setUserData(fetchedUserData);
        setPublicUserData(fetchedPublicUserData);
      }
    } catch (error) {
      debug.error('Error during sign in:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      debug.log('Auth state changed. User:', {
        uid: user?.uid,
        email: user?.email,
        emailVerified: user?.emailVerified,
        displayName: user?.displayName
      });
      setCurrentUser(user);
      if (user) {
        try {
          const { userData: fetchedUserData, publicUserData: fetchedPublicUserData } = 
            await fetchUserDataWithRetry(user.uid);
          
          setUserData(fetchedUserData);
          setPublicUserData(fetchedPublicUserData);
        } catch (error) {
          debug.error('Error fetching user data:', error);
        }
      } else {
        setUserData(null);
        setPublicUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    publicUserData,
    loading,
    signIn: handleSignIn,
    signOut,
    setUserData,
    setPublicUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 